import React, { useState, useEffect } from 'react';
import '../styles/ResetPassword.css';
import '../styles/AuthForm.css'; // Für Sprachumschaltung Styles
import passwordResetApi from '../services/passwordResetApi';
import { useLanguage } from '../contexts/LanguageContext';

const ResetPassword = ({ onSuccess, onShowLogin }) => {
    const { t, language, switchLanguage } = useLanguage();
    const [token, setToken] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [message, setMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [errors, setErrors] = useState({});
    const [isValidToken, setIsValidToken] = useState(false);
    const [isCheckingToken, setIsCheckingToken] = useState(true);

    // Extract token from URL parameters
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const tokenParam = urlParams.get('token');
        
        if (tokenParam) {
            setToken(tokenParam);
            validateToken(tokenParam);
        } else {
            setMessage(t('invalidResetToken'));
            setIsCheckingToken(false);
        }
    }, []);

    const validateToken = async (tokenToValidate) => {
        setIsCheckingToken(true);
        try {
            const response = await passwordResetApi.validateResetToken(tokenToValidate);
            setIsValidToken(response.isValid);
            if (!response.isValid) {
                setMessage(response.message || t('invalidResetToken'));
            }
        } catch (error) {
            setIsValidToken(false);
            setMessage(t('passwordResetError'));
        } finally {
            setIsCheckingToken(false);
        }
    };

    const validatePassword = (password) => {
        const errors = [];
        if (password.length < 6) {
            errors.push('mindestens 6 Zeichen');
        }
        if (!/[a-zA-Z]/.test(password)) {
            errors.push('mindestens einen Buchstaben');
        }
        if (!/[0-9]/.test(password)) {
            errors.push('mindestens eine Zahl');
        }
        return errors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrors({});
        setMessage('');

        // Validation
        const newErrors = {};
        
        if (!newPassword) {
            newErrors.newPassword = t('passwordRequired');
        } else {
            const passwordErrors = validatePassword(newPassword);
            if (passwordErrors.length > 0) {
                newErrors.newPassword = t('passwordTooShort');
            }
        }

        if (!confirmPassword) {
            newErrors.confirmPassword = t('passwordRequired');
        } else if (newPassword !== confirmPassword) {
            newErrors.confirmPassword = t('passwordsDoNotMatch');
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        setIsLoading(true);

        try {
            const response = await passwordResetApi.resetPassword(token, newPassword);
            setIsSuccess(true);
            setMessage(response.message || t('passwordUpdatedMessage'));
            
            // Redirect to login after success
            if (onSuccess) {
                setTimeout(() => onSuccess(), 2000);
            } else {
                setTimeout(() => {
                    if (onShowLogin) {
                        onShowLogin();
                    } else {
                        window.location.href = '/';
                    }
                }, 2000);
            }
        } catch (error) {
            setIsSuccess(false);
            setMessage(error.message || t('passwordResetError'));
        } finally {
            setIsLoading(false);
        }
    };

    const handleBackToLogin = () => {
        if (onShowLogin) {
            onShowLogin();
        } else {
            window.location.href = '/';
        }
    };

    if (isCheckingToken) {
        return (
            <div className="reset-password-container">
                <div className="reset-password-card">
                    {/* Language Switcher */}
                    <div className="auth-language-switcher">
                        <button
                            className={`auth-language-btn ${language === 'en' ? 'active' : ''}`}
                            onClick={() => switchLanguage('en')}
                            title="English"
                            type="button"
                        >
                            🇺🇸
                        </button>
                        <button
                            className={`auth-language-btn ${language === 'de' ? 'active' : ''}`}
                            onClick={() => switchLanguage('de')}
                            title="Deutsch"
                            type="button"
                        >
                            🇩🇪
                        </button>
                    </div>
                    
                    <div className="reset-password-header">
                        <h2>{t('loading')}</h2>
                    </div>
                    <div className="loading-spinner"></div>
                </div>
            </div>
        );
    }

    if (!isValidToken) {
        return (
            <div className="reset-password-container">
                <div className="reset-password-card">
                    {/* Language Switcher */}
                    <div className="auth-language-switcher">
                        <button
                            className={`auth-language-btn ${language === 'en' ? 'active' : ''}`}
                            onClick={() => switchLanguage('en')}
                            title="English"
                            type="button"
                        >
                            🇺🇸
                        </button>
                        <button
                            className={`auth-language-btn ${language === 'de' ? 'active' : ''}`}
                            onClick={() => switchLanguage('de')}
                            title="Deutsch"
                            type="button"
                        >
                            🇩🇪
                        </button>
                    </div>
                    
                    <div className="reset-password-header">
                        <h2>{t('invalidResetToken')}</h2>
                    </div>
                    <div className="reset-password-content">
                        <div className="error-icon">⚠️</div>
                        <p className="error-message">{message}</p>
                        <p className="info-text">
                            {t('passwordResetError')}
                        </p>
                        <div className="reset-password-actions">
                            <button 
                                type="button" 
                                className="back-to-login-btn"
                                onClick={handleBackToLogin}
                            >
                                {t('backToLogin')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (isSuccess) {
        return (
            <div className="reset-password-container">
                <div className="reset-password-card">
                    {/* Language Switcher */}
                    <div className="auth-language-switcher">
                        <button
                            className={`auth-language-btn ${language === 'en' ? 'active' : ''}`}
                            onClick={() => switchLanguage('en')}
                            title="English"
                            type="button"
                        >
                            🇺🇸
                        </button>
                        <button
                            className={`auth-language-btn ${language === 'de' ? 'active' : ''}`}
                            onClick={() => switchLanguage('de')}
                            title="Deutsch"
                            type="button"
                        >
                            🇩🇪
                        </button>
                    </div>
                    
                    <div className="reset-password-header">
                        <h2>{t('passwordUpdatedTitle')}</h2>
                    </div>
                    <div className="reset-password-content">
                        <div className="success-icon">✓</div>
                        <p className="success-message">{message}</p>
                        <p className="info-text">
                            {t('proceedToLogin')}
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="reset-password-container">
            <div className="reset-password-card">
                {/* Language Switcher */}
                <div className="auth-language-switcher">
                    <button
                        className={`auth-language-btn ${language === 'en' ? 'active' : ''}`}
                        onClick={() => switchLanguage('en')}
                        title="English"
                        type="button"
                    >
                        🇺🇸
                    </button>
                    <button
                        className={`auth-language-btn ${language === 'de' ? 'active' : ''}`}
                        onClick={() => switchLanguage('de')}
                        title="Deutsch"
                        type="button"
                    >
                        🇩🇪
                    </button>
                </div>
                
                <div className="reset-password-header">
                    <h2>{t('resetPasswordTitle')}</h2>
                    <p>{t('forgotPasswordDescription')}</p>
                </div>

                <form onSubmit={handleSubmit} className="reset-password-form">
                    <div className="form-group">
                        <label htmlFor="newPassword">{t('newPassword')}</label>
                        <input
                            type="password"
                            id="newPassword"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            className={errors.newPassword ? 'error' : ''}
                            placeholder={t('newPasswordPlaceholder')}
                            disabled={isLoading}
                            autoFocus
                        />
                        {errors.newPassword && <span className="error-text">{errors.newPassword}</span>}
                    </div>

                    <div className="form-group">
                        <label htmlFor="confirmPassword">{t('confirmNewPassword')}</label>
                        <input
                            type="password"
                            id="confirmPassword"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            className={errors.confirmPassword ? 'error' : ''}
                            placeholder={t('confirmNewPasswordPlaceholder')}
                            disabled={isLoading}
                        />
                        {errors.confirmPassword && <span className="error-text">{errors.confirmPassword}</span>}
                    </div>

                    <div className="password-requirements">
                        <p>Passwort-Anforderungen:</p>
                        <ul>
                            <li className={newPassword.length >= 6 ? 'valid' : ''}>Mindestens 6 Zeichen</li>
                            <li className={/[a-zA-Z]/.test(newPassword) ? 'valid' : ''}>Mindestens ein Buchstabe</li>
                            <li className={/[0-9]/.test(newPassword) ? 'valid' : ''}>Mindestens eine Zahl</li>
                        </ul>
                    </div>

                    {message && (
                        <div className={`message ${isSuccess ? 'success' : 'error'}`}>
                            {message}
                        </div>
                    )}

                    <div className="reset-password-actions">
                        <button 
                            type="submit" 
                            className="reset-btn"
                            disabled={isLoading}
                        >
                            {isLoading ? t('updatingPassword') : t('updatePassword')}
                        </button>
                        
                        <button 
                            type="button" 
                            className="back-btn"
                            onClick={handleBackToLogin}
                            disabled={isLoading}
                        >
                            {t('cancel')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ResetPassword;